/**
 * GlossaryTerm — a tappable plain-English explainer for a bit of nautical
 * shorthand. Wrap a label with it and the word gets a faint dotted underline;
 * tap it and a small popover shows the term spelled out with a one-line
 * definition, in our teaching voice. Tap anywhere else, hit the close, or press
 * Escape to dismiss.
 *
 *   <GlossaryTerm termKey="cpa">CPA</GlossaryTerm>
 *
 * It's deliberately quiet: the underline is faint so wiring a label doesn't shout
 * for attention, and the affordance only pays off when someone actually wonders
 * what a term means. It's built to sit anywhere, over the chart or inside a
 * sheet, so it renders the popover through a portal on the document body (above
 * everything) and positions it against the tapped word, clamped to stay on
 * screen. If the key isn't in the glossary it just renders the children as plain
 * text, so a bad key can never break a label.
 *
 * A `select` haptic fires on open, the same light buzz a normal tap gives, so a
 * gloved hand feels the tap land without looking.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { getGlossaryEntry } from './glossary';
import { haptic } from '@/ui/haptics';

/** Gap between the tapped word and the popover, and the min margin off any edge. */
const GAP = 8;
const MARGIN = 10;
const POPOVER_W = 260;

interface Anchor {
  left: number;
  right: number;
  top: number;
  bottom: number;
  cx: number;
}

/**
 * Work out where to put the popover: centred under the tapped word by default,
 * flipped above when there isn't room below, and pulled back inside both edges so
 * it can never hang off screen. Pure, so the placement can be unit-tested without
 * a DOM.
 */
export function placeGlossaryPopover(
  anchor: Anchor,
  w: number,
  h: number,
  vw: number,
  vh: number,
): { left: number; top: number } {
  const left = Math.max(MARGIN, Math.min(vw - w - MARGIN, anchor.cx - w / 2));
  // Below the word if it fits, otherwise above it.
  const belowTop = anchor.bottom + GAP;
  const fitsBelow = belowTop + h <= vh - MARGIN;
  const top = fitsBelow
    ? belowTop
    : Math.max(MARGIN, anchor.top - GAP - h);
  return { left, top };
}

export interface GlossaryTermProps {
  /** Which glossary entry to explain. */
  termKey: string;
  /** The label text to show and underline. */
  children: ReactNode;
  /** Optional extra class on the trigger, if a caller needs to nudge the layout. */
  className?: string;
}

export function GlossaryTerm({ termKey, children, className }: GlossaryTermProps) {
  const entry = getGlossaryEntry(termKey);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();

  const close = useCallback(() => {
    setOpen(false);
    setPos(null);
    // Hand focus back to the word we opened from, so keyboard users don't lose
    // their place.
    triggerRef.current?.focus();
  }, []);

  const activate = useCallback(() => {
    if (!entry) return;
    haptic('select');
    setOpen(true);
  }, [entry]);

  // Measure the popover once it's rendered and clamp it fully on screen. Kept
  // hidden (pos === null) until then, so it never flashes in the wrong spot.
  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    if (!trigger) return;
    const r = trigger.getBoundingClientRect();
    const pop = popRef.current;
    const w = pop?.offsetWidth ?? POPOVER_W;
    const h = pop?.offsetHeight ?? 120;
    const anchor: Anchor = {
      left: r.left,
      right: r.right,
      top: r.top,
      bottom: r.bottom,
      cx: r.left + r.width / 2,
    };
    setPos(placeGlossaryPopover(anchor, w, h, window.innerWidth, window.innerHeight));
  }, [open]);

  // While open, dismiss on Escape, and reposition-by-closing on scroll or resize
  // so the card can't drift away from its word. Outside taps are caught by the
  // transparent catcher below rather than a document listener, so it works even
  // over the chart canvas.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    };
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      activate();
    }
  };

  // No entry for this key: render the label as plain text so nothing breaks.
  if (!entry) return <>{children}</>;

  return (
    <>
      <span
        ref={triggerRef}
        className={`acp-gloss-term${className ? ` ${className}` : ''}`}
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={(e) => {
          // Don't let the tap fall through to whatever the label sits inside (a
          // data-field button, a form <label>, and so on).
          e.preventDefault();
          e.stopPropagation();
          activate();
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        {children}
      </span>
      {open &&
        createPortal(
          <div
            className="acp-gloss-catcher"
            onPointerDown={(e) => {
              e.stopPropagation();
              close();
            }}
          >
            <div
              ref={popRef}
              className="acp-gloss-pop"
              role="dialog"
              aria-labelledby={titleId}
              style={{
                left: pos?.left ?? 0,
                top: pos?.top ?? 0,
                width: POPOVER_W,
                visibility: pos ? 'visible' : 'hidden',
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="acp-gloss-head">
                <span className="acp-gloss-name" id={titleId}>
                  {entry.term}
                </span>
                <button
                  type="button"
                  className="acp-gloss-close"
                  aria-label="Close explanation"
                  onClick={(e) => {
                    e.stopPropagation();
                    close();
                  }}
                >
                  ×
                </button>
              </div>
              <p className="acp-gloss-short">{entry.short}</p>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
